import { AddCircleOutline, MoreVert, Search } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  OutlinedInput,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { FC, MouseEvent, SyntheticEvent, useEffect, useState } from "react";

import TableFooter from "./TableFooter/TableFooter";
import styles from "../IrbSubmission.module.scss";
import {
  useGetAllIrbSubmissionsLazyQuery,
  useRemoveIrbMutation,
} from "../../../../../generated/graphql";
import Moment from "../../../../common/Moment/Moment";
import { useNavigate } from "react-router-dom";
import {
  IRB_SUBMISSION_TYPES,
  ROLES,
  RoutingList,
} from "../../../../../constants";
import useAppStore from "../../../../../states/zustand/appStore";
import WithPermission from "../../../../common/WithPermission/WithPermission";
import { toast } from "react-toastify";
import ConfirmationModal from "../../../../common/confirmationModal/ConfirmationModal";
import useDebounce from "../../../../../hooks/useDebounce";

const IRBSubmissionsTable: FC = (): JSX.Element => {
  const [moreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const openConfirmationModal = () => {
    setShowConfirmationModal(true);
  };

  const closeConfirmationModal = () => {
    setShowConfirmationModal(false);
  };

  const deleteHandler = async () => {
    // await handleDelete(item._id);
    handleDelete();
    setShowConfirmationModal(false);
  };

  const [getAllIrb, { data, loading }] = useGetAllIrbSubmissionsLazyQuery();
  const [removeIrb] = useRemoveIrbMutation();
  const { setLoader } = useAppStore();
  const [debouncedValue, value, setValue] = useDebounce<string>("", 1000);

  const inputHandler = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e?.target?.value);
  };

  useEffect(() => {
    const getAllIrbSub = async () => {
      await getAllIrb({
        variables: {
          type: IRB_SUBMISSION_TYPES.INITIAL_SUBMISSION,
          page: page + 1,
          perPage: rowsPerPage,
          keyword: debouncedValue,
        },
      });
    };
    getAllIrbSub();
  }, [page, rowsPerPage, getAllIrb, debouncedValue]);

  useEffect(() => {
    setLoader(loading);
  }, [loading]);
  const navigate = useNavigate();
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };
  const handleMoreOpen = (event: MouseEvent<HTMLElement>, id: string) => {
    setSelectedId(id);
    setMoreAnchorEl(event.currentTarget);
  };
  const handleMoreClose = () => {
    setMoreAnchorEl(null);
  };
  const handleView = () => {
    navigate(RoutingList.irb.details.path.replace(":id", selectedId));
  };

  const handleEdit = () => {
    navigate(RoutingList.irb.edit.path.replace(":id", selectedId));
  };

  const handleDelete = async () => {
    try {
      let res = await removeIrb({
        variables: {
          _id: selectedId,
        },
      });
      if (!res.data) throw null;
      getAllIrb({
        variables: {
          type: IRB_SUBMISSION_TYPES.INITIAL_SUBMISSION,
          page: 1,
          perPage: rowsPerPage,
          keyword: debouncedValue,
        },
      });
      toast.success("Deleted successfully!");
    } catch (error) {
      toast.error("Could not delete!");
    } finally {
      handleMoreClose();
    }
  };

  return (
    <>
      <Container maxWidth={false}>
        <Card>
          <CardContent className="pd-x-0 pd-y-0">
            <Box className="d-flex align-items-center justify-content-between pd-x-32 pd-t-32 pd-b-20">
              <Typography variant="h2" component="h2">
                All MIRB Submissions
              </Typography>
              <OutlinedInput
                id="outlined-adornment-weight"
                name="searchField"
                value={value}
                onChange={inputHandler}
                endAdornment={
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                }
                aria-describedby="outlined-weight-helper-text"
                inputProps={{
                  "aria-label": "weight",
                }}
                className={"inputField"}
              />
              <WithPermission roles={[ROLES.INVESTIGATOR]}>
                <Button
                  size={"small"}
                  variant={"contained"}
                  color={"error"}
                  onClick={() => navigate(RoutingList.irb.create.path)}
                  type="button"
                >
                  <AddCircleOutline
                    className="mg-r-10"
                    sx={{ width: ".875rem", height: ".875rem" }}
                  />
                  New Submission
                </Button>
              </WithPermission>
            </Box>
            <TableContainer className="custom-table">
              <Table sx={{ minWidth: 650 }} aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell className="pd-l-32">Study Title</TableCell>
                    <TableCell className="pd-l-32">
                      Principal Investigator
                    </TableCell>
                    <TableCell className="pd-l-32">Department</TableCell>
                    <TableCell>Sponsor</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Submission Date</TableCell>
                    <TableCell>Study Type</TableCell>
                    <TableCell className="pd-r-32"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data?.getAllIRBSubmissions?.irbs?.map((item) => (
                    <TableRow
                      sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                      key={item._id}
                      className={
                        item.isAllDocReadAndAcknowledged ? "rowGreen" : "rowRed"
                      }
                    >
                      <TableCell className="pd-l-32">
                        <Typography
                          variant={"subtitle1"}
                          className="font-weight-semi-bold"
                        >
                          <p className="ellipsis">{item.title}</p>
                        </Typography>
                      </TableCell>
                      <TableCell>{item.principalInvestigator}</TableCell>
                      <TableCell>{item.department}</TableCell>

                      <TableCell>{item.sponsor}</TableCell>
                      <TableCell>{item.emails.join(", ")}</TableCell>
                      <TableCell>
                        <Moment date={item.dateOfIRBSubmission} format="ll" />
                      </TableCell>
                      <TableCell>{item.typeOfStudy}</TableCell>
                      <TableCell className="pd-r-32">
                        <IconButton
                          onClick={(e) => handleMoreOpen(e, item._id)}
                          sx={{ p: 0 }}
                        >
                          <MoreVert />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TableFooter
              count={data?.getAllIRBSubmissions?.totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardContent>
        </Card>

        <Menu
          className={styles.menu}
          id="more-menu"
          anchorEl={moreAnchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          keepMounted
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          open={Boolean(moreAnchorEl)}
          onClose={handleMoreClose}
        >
          <MenuItem onClick={handleView}>
            <Typography textAlign="center">View</Typography>
          </MenuItem>
          <WithPermission roles={[ROLES.INVESTIGATOR]}>
            <>
              <MenuItem onClick={handleEdit}>
                <Typography textAlign="center">Edit</Typography>
              </MenuItem>
              <MenuItem onClick={openConfirmationModal}>
                <Typography textAlign="center">Delete</Typography>
              </MenuItem>
            </>
          </WithPermission>
        </Menu>
      </Container>

      <ConfirmationModal
        submitText={"Delete"}
        open={showConfirmationModal}
        onConfirm={deleteHandler}
        onClose={closeConfirmationModal}
        description="Are you sure to delete this? You can not revert the changes."
      />
    </>
  );
};

export default IRBSubmissionsTable;
